/**
 * Sheet and Step Styles
 *
 * This file contains layout, typography, spacing, and structural styles for:
 * - Sheets and modals
 * - Step components
 * - Form layouts
 * - Threshold tables and pricing components
 * - Interactive elements (buttons, selects, etc.)
 * - Typography and text styling
 *
 * For input element specific styles (borders, padding, etc.),
 * see /shared/components/inputs/styles.ts
 */

// Layout and spacing
export const LAYOUT = {
  section: "space-y-4",
  sectionContainer: "space-y-1.5",
  field: "space-y-2",
  indent: "ml-6",
} as const

// Typography
export const TYPOGRAPHY = {
  label: "text-xs font-medium leading-tight",
  labelLarge: "text-sm text-foreground font-medium leading-none tracking-tight",
  sectionHeader: "text-lg font-medium",
  description: "text-xs text-teal-300 font-light leading-tight tracking-tight",
  descriptionLarge: "text-sm text-teal-300 font-light leading-tight tracking-tight",
  itemLabel: "text-sm font-normal leading-tight",
  link: "text-xs text-primary hover:underline font-light leading-tight tracking-tight",
  checkboxLabel: "text-sm font-medium",
  checkboxDescription: "text-xs text-muted-foreground",
  phaseHeader: "text-sm font-mono font-light leading-none tracking-tight text-[#8FA5A9] uppercase",
} as const

// Interactive elements
export const INTERACTIVE = {
  removeButton: "opacity-0 group-hover:opacity-100 transition-opacity",
  selectTrigger: "h-10",
  button: "flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors",
  buttonBase: "flex-1 flex gap-2 items-center justify-start px-3 py-1 rounded-lg transition-all",
  linkButton: "text-xs text-primary hover:underline font-light leading-tight tracking-tight",
} as const

// Input field components
export const INPUTS = {
  base: "[border-radius:10px] placeholder:font-light placeholder:opacity-50",
  numberInput: "[border-radius:10px] pr-8 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]",
} as const

// Threshold table system (used by ThresholdRow and pricing tables)
export const THRESHOLD = {
  // Field sizes
  fields: {
    tiny: "flex-none w-[3.75rem]",     // Inventory, counters
    small: "flex-none w-[6.875rem]",   // Price overrides
    medium: "flex-none w-[8.75rem]",   // Currency amounts
    large: "flex-1 max-w-[10.875rem]", // Text inputs, descriptions
  },
  // Layout components
  layout: {
    tableHeaders: "flex items-center gap-2 text-xs font-mono text-teal-300 tracking-wide uppercase",
    headerText: "text-xs font-mono text-teal-300 tracking-wide uppercase",
    rowContainer: "space-y-2",
    separator: "flex-none h-4 w-4 text-muted-foreground",
    removeButtonSpace: "flex-none w-6",
  },
} as const

// Checkbox layout components
export const CHECKBOX = {
  container: "flex items-start gap-2",
  textContainer: "flex flex-col gap-1",
  checkboxLabel: "text-sm font-medium",
  checkboxDescription: "text-xs text-muted-foreground",
} as const

// Compound input layout (for forms with multi-part inputs)
export const COMPOUND_INPUT = {
  wrapper: "flex relative rounded-[10px] ring-1 ring-input transition-all focus-within:ring-foreground",
  labelSectionBase: "flex items-center justify-center px-3 bg-white rounded-l-[10px] border-r border-input",
  labelSectionNarrow: "w-12",
  labelSectionWide: "w-24",
  inputSection: "border-0 border-r border-input focus:outline-none focus:border-input",
  selectSection: "border-0 focus:outline-none focus:ring-0",
} as const

// Entity card components (used by SummaryCard)
export const ENTITY_CARD = {
  container: "bg-[#F4F6F6] rounded-lg p-4",
  layout: "flex items-center justify-between",
  infoContainer: "flex flex-col",
  actionButton: "text-primary hover:text-primary/80 h-auto p-0",
} as const

// Phase editor styles
export const PHASE_EDITOR = {
  container: "bg-[#F8F9F9] rounded-lg p-4",
  buttonCancel: "rounded-full h-8 text-foreground px-4 hover:bg-muted font-light",
  buttonSaveAndAdd: "rounded-full h-8 bg-background border-foreground text-foreground px-4 hover:bg-muted font-light",
  buttonSave: "rounded-full h-8 bg-foreground hover:bg-foreground/90 text-background px-4 shadow-lg font-light",
  buttonAddAnother: "rounded-full h-10 bg-background border-foreground text-foreground px-4 hover:bg-muted font-normal",
} as const

// Saved phase card styles (used by billing schedules and subscription plans)
export const SAVED_PHASE_CARD = {
  container: "bg-[#F8F9F9] rounded-lg p-4 max-w-sm",
  layout: "flex items-center justify-between",
  infoContainer: "flex flex-col",
  menuButton: "h-8 w-8 p-0 text-foreground hover:bg-transparent",
  menuIcon: "h-5 w-5",
} as const
